import {
    ENGRAVING_CHANNEL,
    LETTERPRESS_CHANNEL,
    LETTERPRESS_MODE_SOFTLIGHT,
    GLOW_CHANNEL,
    DEFAULT_GLOW_R,
    DEFAULT_GLOW_G,
    DEFAULT_GLOW_B,
    DEFAULT_GLOW_RADIUS,
    DEFAULT_GLOW_LIGHT,
    GLOW_RADIUS_MIN,
    GLOW_RADIUS_MAX,
    LETTERPRESS_DEPTHS,
} from './constants';

/**
 * Effect type enum for type-safe effect handling
 */
export enum EffectType {
    NONE = 'none',
    ENGRAVING = 'Engraving',
    LETTERPRESS = 'Letterpress',
    GLOW = 'Glow',
}

/**
 * Base effect interface - all effects extend this
 */
export interface BaseEffect {
    type: EffectType;
}

/**
 * Engraving effect properties
 */
export interface EngravingEffect extends BaseEffect {
    type: EffectType.ENGRAVING;
    color: string;
}

/**
 * Letter Press effect properties
 */
export interface LetterPressEffect extends BaseEffect {
    type: EffectType.LETTERPRESS;
    mode: 'softlight' | 'overlay';
    depth: number;
}

/**
 * Glow effect properties
 */
export interface GlowEffect extends BaseEffect {
    type: EffectType.GLOW;
    r: number;
    g: number;
    b: number;
    radius: number;
    light: boolean;
}

/**
 * Discriminated union for all possible effect types
 * Using null instead of 'none' for cleaner type checking
 */
export type DocumentEffect = EngravingEffect | LetterPressEffect | GlowEffect | null;

/**
 * Effect configuration metadata
 */
export interface EffectConfig<T extends BaseEffect> {
    type: EffectType;
    channel: string;
    label: string;
    createDefault: () => T;
    validate?: (effect: T) => boolean;
}

/**
 * Effect registry - centralized configuration for all effects
 */
export const EFFECT_CONFIGS: {
    [EffectType.ENGRAVING]: EffectConfig<EngravingEffect>;
    [EffectType.LETTERPRESS]: EffectConfig<LetterPressEffect>;
    [EffectType.GLOW]: EffectConfig<GlowEffect>;
} = {
    [EffectType.ENGRAVING]: {
        type: EffectType.ENGRAVING,
        channel: ENGRAVING_CHANNEL,
        label: 'Engraving',
        createDefault: () => ({
            type: EffectType.ENGRAVING,
            color: '#808080',
        }),
    },
    [EffectType.LETTERPRESS]: {
        type: EffectType.LETTERPRESS,
        channel: LETTERPRESS_CHANNEL,
        label: 'Letter Press',
        createDefault: () => ({
            type: EffectType.LETTERPRESS,
            mode: LETTERPRESS_MODE_SOFTLIGHT,
            depth: 2,
        }),
        validate: (effect) => effect.depth >= 1 && effect.depth <= LETTERPRESS_DEPTHS.length,
    },
    [EffectType.GLOW]: {
        type: EffectType.GLOW,
        channel: GLOW_CHANNEL,
        label: 'Glow',
        createDefault: () => ({
            type: EffectType.GLOW,
            r: DEFAULT_GLOW_R,
            g: DEFAULT_GLOW_G,
            b: DEFAULT_GLOW_B,
            radius: DEFAULT_GLOW_RADIUS,
            light: DEFAULT_GLOW_LIGHT,
        }),
        validate: (effect) => effect.radius >= GLOW_RADIUS_MIN && effect.radius <= GLOW_RADIUS_MAX,
    },
};

/**
 * Get effect configuration by type
 */
export function getEffectConfig(
    type: EffectType.ENGRAVING | EffectType.LETTERPRESS | EffectType.GLOW,
): EffectConfig<any> {
    return EFFECT_CONFIGS[type];
}

/**
 * Get effect channel name for XML serialization
 */
export function getEffectChannel(effect: DocumentEffect): string | null {
    if (!effect) return null;
    const config = EFFECT_CONFIGS[effect.type];
    return config?.channel || null;
}
